import { useEffect, useId, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WorkspaceInput, workspaceInputClass } from '@/components/ui/workspace/field';
import { cn } from '@/lib/utils/cn';
import {
  applyScoringModelRows,
  emptyScoringDomainRow,
  formatEditedValue,
  hasScoringModelForm,
  parseEditedValue,
  scoringModelRows,
  weightTotalText,
  type ScoringDomainRow,
} from './scoring-model';

/**
 * The proposed value a reviewer approves "as noted" (C2-36), for the
 * review queue and the document review pane alike.
 *
 *   - A scoring-model (`methodology`) proposal gets a small form: one row
 *     per domain with its name and weight, add / remove, and the weights'
 *     total under the rows.
 *   - Every type keeps the JSON editor, behind an "Advanced" disclosure.
 *     JSON that does not parse says so inline as it is typed, and the
 *     caller refuses to submit it (`validateEditedValue`); nothing fails
 *     silently.
 *
 * The JSON text is the one value (`json` / `onJsonChange`): the form writes
 * its rows into it, and a valid JSON edit re-fills the rows.
 */
export interface ProposedValueEditorProps {
  proposalType: string;
  /** The value being submitted, as JSON text. */
  json: string;
  onJsonChange: (json: string) => void;
  /** The problem with the value, shown under the editor; null when none. */
  error: string | null;
  onErrorChange: (error: string | null) => void;
  className?: string;
}

export function ProposedValueEditor({
  proposalType,
  json,
  onJsonChange,
  error,
  onErrorChange,
  className,
}: ProposedValueEditorProps) {
  const showForm = hasScoringModelForm(proposalType);
  const id = useId();
  const [rows, setRows] = useState<ScoringDomainRow[]>(() => {
    const parsed = parseEditedValue(json);
    return 'value' in parsed ? scoringModelRows(parsed.value) : [];
  });
  // Rows follow a JSON edit that parses; a JSON edit that does not leaves
  // the last good rows in place until it is fixed.
  const [lastJsonFromRows, setLastJsonFromRows] = useState(json);
  useEffect(() => {
    if (!showForm || json === lastJsonFromRows) return;
    const parsed = parseEditedValue(json);
    if ('value' in parsed) setRows(scoringModelRows(parsed.value));
  }, [json, lastJsonFromRows, showForm]);

  function updateRows(next: ScoringDomainRow[]) {
    setRows(next);
    const parsed = parseEditedValue(json);
    const base = 'value' in parsed ? parsed.value : {};
    const text = formatEditedValue(applyScoringModelRows(base, next));
    setLastJsonFromRows(text);
    onJsonChange(text);
    onErrorChange(null);
  }

  function onJsonTyped(text: string) {
    onJsonChange(text);
    const parsed = parseEditedValue(text);
    onErrorChange('error' in parsed ? parsed.error : null);
  }

  const total = weightTotalText(rows);

  return (
    <div className={cn('min-w-0', className)}>
      {showForm ? (
        <fieldset className="min-w-0 border-0 p-0">
          <legend className="mb-1.5 text-meta font-medium text-ink-700">Domains and weights</legend>
          <div className="flex flex-col gap-1.5">
            {rows.length === 0 ? (
              <p className="text-meta text-ink-500">No domains yet. Add one below.</p>
            ) : (
              <div className="grid grid-cols-[minmax(0,1fr)_96px_32px] gap-1.5 text-meta text-ink-500">
                <span>Domain</span>
                <span>Weight</span>
                <span className="sr-only">Remove</span>
              </div>
            )}
            {rows.map((row, i) => (
              <div
                key={row.key}
                className="grid grid-cols-[minmax(0,1fr)_96px_32px] items-center gap-1.5"
              >
                <WorkspaceInput
                  id={`${id}-name-${i}`}
                  aria-label={`Domain ${i + 1} name`}
                  value={row.name}
                  placeholder="e.g. Data Migration"
                  onChange={(e) =>
                    updateRows(
                      rows.map((r) => (r.key === row.key ? { ...r, name: e.target.value } : r)),
                    )
                  }
                />
                <WorkspaceInput
                  id={`${id}-weight-${i}`}
                  aria-label={`Domain ${i + 1} weight`}
                  inputMode="decimal"
                  value={row.weight}
                  placeholder="0.20"
                  onChange={(e) =>
                    updateRows(
                      rows.map((r) => (r.key === row.key ? { ...r, weight: e.target.value } : r)),
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove domain ${i + 1}`}
                  onClick={() => updateRows(rows.filter((r) => r.key !== row.key))}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => updateRows([...rows, emptyScoringDomainRow()])}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Add domain
              </Button>
              {total ? (
                <span className="text-meta text-ink-500" data-testid="weight-total">
                  {total}
                </span>
              ) : null}
            </div>
          </div>
        </fieldset>
      ) : null}

      <details className={cn('group', showForm && 'mt-3')}>
        <summary className="cursor-pointer text-meta text-ink-500 hover:text-ink-700">
          Advanced: edit as JSON
        </summary>
        <textarea
          aria-label="Proposed value as JSON"
          value={json}
          onChange={(e) => onJsonTyped(e.target.value)}
          rows={8}
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          className={cn(
            workspaceInputClass,
            'mt-1.5 h-auto resize-y py-2 font-mono text-meta leading-5',
            error && 'border-posture-critical',
          )}
        />
      </details>

      {error ? (
        <p role="alert" className="mt-1.5 text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </div>
  );
}
